import { AlertComponent } from "../components/modals/alert/alert.component";
import { Injectable } from "@angular/core";
import { AlertController, LoadingController, ModalController, NavController, PopoverController, ToastController } from "@ionic/angular";
import { SharedService } from "./shared.service";
import { LinkService } from "./link.service";
import { Subject } from "rxjs";
import { SettingsType } from "../models/settings";
import { User, UserBlocking } from "@app/models/user";
import { AnalyticsService } from "@app/core/analytics";
import type { UiMessageAction } from "@app/core/analytics";

@Injectable({
  providedIn: "root",
})
export class SystemService {
  //efeito blur para modais
  blur = false;

  closeModal$ = new Subject<void>();

  enablePP = false;
  enableCheckExpress = false;
  enableEditOrder = false;
  enablePromotions = false;

  constructor(
    public navCtrl: NavController,
    public toastCtrl: ToastController,
    public alertCtrl: AlertController,
    public loadingCtrl: LoadingController,
    public modalCtrl: ModalController,
    public popoverCtrl: PopoverController,
    public shared: SharedService,
    public link: LinkService,
    private analyticsService: AnalyticsService
  ) {
    this.getSettings();
  }

  getSettings() {
    this.shared.get<SettingsType>("settings").subscribe({
      next: (res) => {
        const settings = res.data;
        this.enablePP = settings?.enablePP ?? false;
        this.enableCheckExpress = settings?.enableCheckExpress ?? false;
        this.enableEditOrder = settings?.enableEditOrder ?? false;
        this.enablePromotions = settings?.enablePromotions ?? false;
      },
      error: (err) => {
        console.error("Erro ao buscar configurações: ", err);
      },
    });
  }

  public async showToast(message: string, duration?: number) {
    // GA4: ui_message (info via toast)
    this.analyticsService.trackUiMessage({
      msg_type: 'info',
      msg_code: 'TOAST',
      msg_text: message,
      ui_component: 'toast',
    });

    const toast = await this.toastCtrl.create({
      message,
      duration: duration ?? 3000,
    });
    await toast.present();
  }

  /**
   * 'none' title color warn | 'default' title color dark
   */
  async showAlert(
    typeImage: "success" | "warn" | "warn-exclamation" | "success-coke" | "default" | "none",
    title: string,
    description: string,
    mainLabel?: string | null,
    mainEvent?: Function | null,
    secondaryLabel?: string | null,
    secondaryEvent?: Function | null,
    hideHelpButton?: boolean,
    showClose?: boolean,
    orderId?: string,
    seeRegulamentation?: boolean,
    uiAction?: UiMessageAction | string
  ) {
    // GA4: ui_message (alert)
    const msgType = typeImage.startsWith('success') ? 'success'
      : typeImage.startsWith('warn') ? 'warning'
      : 'info';
    this.analyticsService.trackUiMessage({
      msg_type: msgType as any,
      msg_code: 'ALERT',
      msg_text: description || title,
      ui_component: 'alert',
      ui_action: uiAction,
    });

    const modal = await this.modalCtrl.create({
      component: AlertComponent,
      backdropDismiss: false,
      cssClass: typeImage,
      componentProps: {
        type: typeImage,
        title: title,
        orderId,
        description: description,
        mainLabel: mainLabel ?? null,
        secondaryLabel: typeImage === "none" && !secondaryLabel && !hideHelpButton ? "Preciso de ajuda" : secondaryLabel ?? null,
        showClose: showClose,
        mainEvent,
        secondaryEvent,
        seeRegulamentation,
      },
    });
    modal.present();
    modal.onDidDismiss().then(({ role }) => {
      //Condição para exibir o "Preciso de ajuda" default
      if (typeImage === "none" && !secondaryLabel && role === "secondary") {
        this.openSupport();
      }
    });
  }

  async openSupport() {
    const loading = await this.loadingCtrl.create({});
    loading.present();
    this.shared.get<{ location: string }>("support/zendesk").subscribe((res) => {
      loading.dismiss();
      if (res.data?.location) {
        this.link.openLink(res.data.location);
      } else {
        this.showErrorAlert(res, "Atenção", true);
      }
    });
  }

  private blockingAlertVisible = false;

  isUserBlocked(user?: User): boolean {
    return !!user?.blocking?.code && !!user?.blocking?.reason;
  }

  async showBlockingAlert(blocking: UserBlocking): Promise<void> {
    if (this.blockingAlertVisible) return;
    this.blockingAlertVisible = true;
    const modal = await this.modalCtrl.create({
      component: AlertComponent,
      backdropDismiss: false,
      cssClass: "none",
      componentProps: {
        type: "none",
        title: `Erro código ${blocking.code}`,
        description: blocking.reason,
        mainLabel: "Voltar",
        secondaryLabel: null,
        showClose: false,
        mainEvent: null,
        secondaryEvent: null,
      },
    });
    await modal.present();
    await modal.onDidDismiss();
    this.blockingAlertVisible = false;
  }

  checkBlockingAndAlert(user?: User): boolean {
    if (this.isUserBlocked(user)) {
      this.showBlockingAlert(user!.blocking!);
      return true;
    }
    return false;
  }

  public async showErrorAlert(response?: any, title?: string, hideHelpButton?) {
    console.error("Ocorreu um erro: ", response);

    let msg = "Ocorreu uma falha de comunicação! Tente novamente mais tarde.";
    const alertTitle = title ?? "Atenção";

    if (response?.message) {
      msg = response.message;
    } else if (response?.error_description) {
      msg = response.error_description;
    } else if (response?.error_message) {
      msg = response.error_message;
    } else if (response?.data?.error_description) {
      msg = response.data.error_description;
    } else if (response?.data?.error_message) {
      msg = response.data.error_message;
    } else if (response?.data?.message) {
      msg = response.data.message;
    }

    this.showAlert("none", alertTitle, msg, "Voltar", null, null, null, hideHelpButton);
  }
}
